import type * as React from 'react'
import { forwardRef } from 'react'

import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { clsx } from 'clsx'

import {
  Avatar,
  ChannelIcon,
  type ChannelType,
  Tooltip,
} from '@buffer-mono/popcorn'

import styles from './ToggleChannels.module.css'

type ToggleChannelsElement = React.ElementRef<
  typeof ToggleGroupPrimitive.ToggleGroup
>
type ToggleChannelsItem = React.ElementRef<typeof ToggleGroupPrimitive.Item>

type ToggleChannelsProps = {
  /**
   * Children are the elements in the toggle group
   */
  children: React.ReactNode
  /**
   * The controlled value of the pressed items
   */
  value?: string[]
  /**
   * The value of the item to show as pressed. Use when you do not need
   * to control the state of the items
   */
  defaultValue?: string[]
  /**
   * When true, prevents the user from interacting with the toggle group and
   * all its items
   * @default false
   */
  disabled?: boolean
  /**
   * Event handler called when the pressed state of an item changes
   */
  onChange?: (value: string[]) => void
  /**
   * Class name applied to the root element
   */
  className?: string
}

/**
 * Custom channel selection component for the composer.
 *
 * Built with Radix ToggleGroup primitives + Popcorn Avatar/ChannelIcon components.
 * Uses a compound component pattern where ToggleChannels.Item has built-in avatar styling,
 * eliminating the need for complex CSS overrides that would be required with the standard
 * Popcorn ToggleGroup + ChannelAvatar combination.
 *
 * @note Composer-specific component. Consider moving to Popcorn if reused elsewhere.
 */
const ToggleChannels = forwardRef<ToggleChannelsElement, ToggleChannelsProps>(
  (props: ToggleChannelsProps, ref) => {
    const {
      children,
      disabled = false,
      className,
      value,
      defaultValue,
      onChange,
    } = props

    return (
      <ToggleGroupPrimitive.Root
        ref={ref}
        disabled={disabled}
        className={clsx(styles.base, className)}
        orientation="horizontal"
        type="multiple"
        value={value}
        defaultValue={defaultValue}
        onValueChange={onChange}
      >
        {children}
      </ToggleGroupPrimitive.Root>
    )
  },
)

ToggleChannels.displayName = 'ToggleChannels'

type ToggleChannelsItemProps = {
  /**
   * The controlled value of the pressed items
   */
  value: string
  /**
   * Source of the avatar image
   */
  src?: string
  /**
   * Alternative text for the avatar image
   */
  alt: string
  /**
   * Channel type for the avatar
   */
  channel: ChannelType
  /**
   * Class name applied to the item element
   */
  className?: string
  /**
   * Click handler
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  /**
   * Whether the channel is selected (maps to Radix data-state="on"/"off")
   */
  isSelected?: boolean
  /**
   * Content for the tooltip
   */
  tooltipContent?: string
}

const ToggleChannelsItem = forwardRef<
  ToggleChannelsItem,
  ToggleChannelsItemProps
>((props, ref) => {
  const {
    value,
    src,
    alt,
    channel,
    className,
    onClick,
    isSelected,
    tooltipContent,
    ...restProps
  } = props

  const avatarContent = (
    <div
      data-channel={channel}
      className={clsx(
        styles.channelAvatarWrapper,
        isSelected ? styles.selected : styles.unselected,
      )}
    >
      <Avatar size="mid" src={src} alt={alt} />
      <div className={styles.channelIcon}>
        <ChannelIcon type={channel} color="inverted" size={12} />
      </div>
    </div>
  )

  return (
    <ToggleGroupPrimitive.Item
      id={value}
      ref={ref}
      value={value}
      className={clsx(styles.item, className)}
      onClick={onClick}
      aria-label={`${channel} channel${isSelected ? ' (selected)' : ''}`}
      {...restProps}
    >
      {tooltipContent ? (
        <Tooltip content={tooltipContent}>{avatarContent}</Tooltip>
      ) : (
        avatarContent
      )}
    </ToggleGroupPrimitive.Item>
  )
})

ToggleChannelsItem.displayName = 'ToggleChannels.Item'

const ToggleChannelsObject = Object.assign(ToggleChannels, {
  Item: ToggleChannelsItem,
})

export { ToggleChannelsObject as ToggleChannels }
export type { ToggleChannelsProps, ToggleChannelsItemProps }
